import React, { useState, useEffect } from 'react';
import { SettingsManagement } from '../../components/admin/organisms';
import { StaffUser, StaffNotificationSettings, DeliveryTier, DeliveryPricingSettings, VatSettings, ImageOnCakeSettings } from '../../types';
import { 
  getStaffUser, 
  updateStaffUser, 
  resetPassword,
  auth,
  getDeliveryTiers,
  createDeliveryTier,
  updateDeliveryTier,
  deleteDeliveryTier,
  getDeliveryPricingSettings,
  updateDeliveryPricingSettings,
  getVatSettings,
  updateVatSettings,
  initializeDefaultVatSettings,
  getImageOnCakeSettings,
  updateImageOnCakeSettings,
  initializeDefaultImageOnCakeSettings
} from '../../services/firebase';
import './SettingsPage.css';

const SettingsPage: React.FC = () => {
  const [staffUser, setStaffUser] = useState<StaffUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Delivery pricing state
  const [deliveryTiers, setDeliveryTiers] = useState<DeliveryTier[]>([]);
  const [deliverySettings, setDeliverySettings] = useState<DeliveryPricingSettings | null>(null);
  
  // VAT settings state
  const [vatSettings, setVatSettings] = useState<VatSettings | null>(null);
  
  // Image on cake settings state
  const [imageOnCakeSettings, setImageOnCakeSettings] = useState<ImageOnCakeSettings | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!auth.currentUser) {
          setError('No authenticated user found');
          setLoading(false);
          return;
        }

        // Fetch staff user, delivery pricing data, VAT settings, and image on cake settings in parallel
        const [user, tiers, settings, vatSettingsData, imageOnCakeSettingsData] = await Promise.all([
          getStaffUser(auth.currentUser.uid),
          getDeliveryTiers(),
          getDeliveryPricingSettings(),
          getVatSettings(),
          getImageOnCakeSettings()
        ]);

        if (user) {
          setStaffUser(user);
        } else {
          setError('User profile not found. Please contact an administrator.');
        }

        setDeliveryTiers(tiers);
        setDeliverySettings(settings);
        
        // Initialize default VAT settings if they don't exist
        if (!vatSettingsData) {
          try {
            const defaultVatSettings = await initializeDefaultVatSettings();
            setVatSettings(defaultVatSettings);
          } catch (initError) {
            console.error('Error initializing default VAT settings:', initError);
            setVatSettings(null);
          }
        } else {
          setVatSettings(vatSettingsData);
        }

        // Initialize default image on cake settings if they don't exist
        if (!imageOnCakeSettingsData) {
          try {
            await initializeDefaultImageOnCakeSettings();
            const defaultImageOnCakeSettings = await getImageOnCakeSettings();
            setImageOnCakeSettings(defaultImageOnCakeSettings);
          } catch (initError) {
            console.error('Error initializing default image on cake settings:', initError);
            setImageOnCakeSettings(null);
          }
        } else {
          setImageOnCakeSettings(imageOnCakeSettingsData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleUpdateProfile = async (userData: Partial<StaffUser>) => {
    if (!staffUser || !auth.currentUser) return;
    
    try {
      setLoading(true);
      await updateStaffUser(staffUser.uid, userData);
      
      // Update local state
      setStaffUser({
        ...staffUser,
        ...userData
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
      setLoading(false);
    }
  };

  const handleUpdateSecurity = async (userData: Partial<StaffUser>) => {
    if (!staffUser || !auth.currentUser) return;
    
    try {
      setLoading(true);
      await updateStaffUser(staffUser.uid, userData);
      
      // Update local state
      setStaffUser({
        ...staffUser,
        ...userData
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating security settings:', err);
      setError('Failed to update security settings. Please try again.');
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!staffUser || !auth.currentUser) return;
    
    try {
      setLoading(true);
      await resetPassword(staffUser.email);
      setLoading(false);
    } catch (err) {
      console.error('Error sending password reset:', err);
      setError('Failed to send password reset email. Please try again.');
      setLoading(false);
    }
  };

  const handleUpdateNotifications = async (settings: StaffNotificationSettings) => {
    if (!staffUser || !auth.currentUser) return;
    
    try {
      setLoading(true);
      await updateStaffUser(staffUser.uid, { notificationSettings: settings });
      
      // Update local state
      setStaffUser({
        ...staffUser,
        notificationSettings: settings
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating notification settings:', err);
      setError('Failed to update notification preferences. Please try again.');
      setLoading(false);
    }
  };

  // Delivery pricing handlers
  const handleCreateDeliveryTier = async (tierData: Partial<DeliveryTier>) => {
    try {
      setLoading(true);
      const newTier = await createDeliveryTier(tierData);
      setDeliveryTiers(prev => [newTier, ...prev]);
      setLoading(false);
    } catch (err) {
      console.error('Error creating delivery tier:', err);
      setError('Failed to create delivery tier. Please try again.');
      setLoading(false);
    }
  };

  const handleUpdateDeliveryTier = async (tierId: string, updates: Partial<DeliveryTier>) => {
    try {
      setLoading(true);
      await updateDeliveryTier(tierId, updates);
      
      // Update local state
      setDeliveryTiers(prev => 
        prev.map(tier => 
          tier.id === tierId 
            ? { ...tier, ...updates, updatedAt: new Date() }
            : tier
        )
      );
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating delivery tier:', err);
      setError('Failed to update delivery tier. Please try again.');
      setLoading(false);
    }
  };

  const handleDeleteDeliveryTier = async (tierId: string) => {
    try {
      setLoading(true);
      await deleteDeliveryTier(tierId);
      
      // Update local state
      setDeliveryTiers(prev => prev.filter(tier => tier.id !== tierId));
      
      setLoading(false);
    } catch (err) {
      console.error('Error deleting delivery tier:', err);
      setError('Failed to delete delivery tier. Please try again.');
      setLoading(false);
    }
  };

  const handleUpdateDeliverySettings = async (settingsData: Partial<DeliveryPricingSettings>) => {
    try {
      setLoading(true);
      await updateDeliveryPricingSettings(settingsData);
      
      // Update local state
      setDeliverySettings(prev => ({
        ...prev,
        ...settingsData,
        updatedAt: new Date()
      } as DeliveryPricingSettings));
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating delivery settings:', err);
      setError('Failed to update delivery settings. Please try again.');
      setLoading(false);
    }
  };

  const handleUpdateVatSettings = async (settingsData: Partial<VatSettings>) => {
    try {
      setLoading(true);
      await updateVatSettings(settingsData);
      
      // Update local state
      setVatSettings(prev => ({
        ...prev,
        ...settingsData,
        updatedAt: new Date()
      } as VatSettings));
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating VAT settings:', err);
      setError('Failed to update VAT settings. Please try again.');
      setLoading(false);
    }
  };

  const handleUpdateImageOnCakeSettings = async (settingsData: Partial<ImageOnCakeSettings>) => {
    try {
      setLoading(true);
      await updateImageOnCakeSettings(settingsData);
      
      // Update local state
      setImageOnCakeSettings(prev => ({
        ...prev,
        ...settingsData,
        updatedAt: new Date()
      } as ImageOnCakeSettings));
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating image on cake settings:', err);
      setError('Failed to update image on cake settings. Please try again.');
      setLoading(false);
    }
  };

  if (loading && !staffUser) {
    return <div className="settings-loading">Loading settings...</div>;
  }

  if (error && !staffUser) {
    return <div className="settings-error">{error}</div>;
  }

  if (!staffUser) {
    return <div className="settings-error">User profile not found</div>;
  }

  return (
    <div className="settings-page">
      {error && (
        <div className="settings-alert">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      
      <SettingsManagement
        user={staffUser}
        loading={loading}
        onUpdateProfile={handleUpdateProfile}
        onUpdateSecurity={handleUpdateSecurity}
        onPasswordReset={handlePasswordReset}
        onUpdateNotifications={handleUpdateNotifications}
        deliveryTiers={deliveryTiers}
        deliverySettings={deliverySettings}
        onCreateDeliveryTier={handleCreateDeliveryTier}
        onUpdateDeliveryTier={handleUpdateDeliveryTier}
        onDeleteDeliveryTier={handleDeleteDeliveryTier}
        onUpdateDeliverySettings={handleUpdateDeliverySettings}
        vatSettings={vatSettings}
        onUpdateVatSettings={handleUpdateVatSettings}
        imageOnCakeSettings={imageOnCakeSettings}
        onUpdateImageOnCakeSettings={handleUpdateImageOnCakeSettings}
      />
    </div>
  );
};

export default SettingsPage;
